<template>
	<div class="view-content">
		<div class="page-heading">
			<h1>Ad Network Performance</h1>
		</div>
		<p-message v-if="!query.filters.network_id" :closable="false">Select a network to begin...</p-message>
		<query-form v-model:query="query" :loading="loading" @apply="getData(true)">
			<template #display>
				<div class="display-value">
					<div class="label">Ad Network:</div>
					<div class="value">
						{{ query.filters.network_id || 'None' }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">Date Range:</div>
					<div class="value">
						{{ formatDate(query.date_range, 'ddd, MMM DD, YYYY') }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">Placement:</div>
					<div class="value">
						{{ displaySelected(query.filters.placement, placement_options) }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">Vertical:</div>
					<div class="value">
						{{ displaySelected(query.filters.vertical_id, $root.appStore.verticalOptions) }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">State:</div>
					<div class="value">
						{{ displaySelected(query.filters.state, state_options) }}
					</div>
				</div>
			</template>
			<template #utils>
				<p-button
					v-tooltip.top="'Refresh'"
					icon="pi pi-refresh"
					aria-label="Refresh"
					class="mr-2"
					@click="getData(true)"
				/>
			</template>
			<gutter size="20px" />
			<template #form="form">
				<row>
					<column max-width="350px">
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Ad Network:</label>
								<div class="controls">
									<div class="field">
										<p-dropdown
											v-model="form.query.filters.network_id"
											:options="ad_network_options"
											option-label="label"
											option-value="value"
											placeholder="Select Ad Network"
										/>
									</div>
								</div>
							</div>
						</div>
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Date Range:</label>
								<div class="controls">
									<div class="field">
										<date-range-picker :max-date_range="62" v-model="form.query.date_range" />
									</div>
								</div>
							</div>
						</div>
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Placement:</label>
								<div class="controls">
									<div class="field">
										<p-dropdown
											v-model="form.query.filters.placement"
											:options="placement_options"
											option-label="label"
											option-value="value"
											placeholder="Select Placement"
										/>
									</div>
								</div>
							</div>
						</div>
					</column>
					<gutter size="40px" />
					<column max-width="350px">
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Vertical:</label>
								<div class="controls">
									<div class="field">
										<p-multi-select
											v-model="form.query.filters.vertical_id"
											:options="$root.appStore.verticalOptions"
											option-label="label"
											option-value="value"
											display-selected-label
											show-clear
											filter
										/>
									</div>
								</div>
							</div>
						</div>
						<div class="control-group">
							<div class="inner">
								<label class="control-label">States:</label>
								<div class="controls">
									<div class="field">
										<p-multi-select
											v-model="form.query.filters.state"
											filter
											filter-placeholder="Filter States"
											:options="state_options"
											option-label="label"
											option-value="value"
										/>
									</div>
								</div>
							</div>
						</div>
					</column>
					<gutter class="hide-mobile" size="40px" />
					<column class="hide-mobile"></column>
				</row>
			</template>
		</query-form>
		<div class="metrics">
			<row>
				<div class="column">
					<metric label="Impressions" :value="metrics.impressions || 0" is-number />
				</div>
				<gutter size="20px" />
				<div class="column">
					<metric label="Clicks" :value="metrics.clicks || 0" is-number />
				</div>
				<gutter size="20px" />
				<div class="column">
					<metric label="Ad Monetization" :value="metrics.ad_revenue || 0" is-currency />
				</div>
				<gutter size="20px" />
				<div class="column">
					<metric label="Revenue/Impression" :value="metrics.rpi || 0" is-currency />
				</div>
				<gutter size="20px" />
				<div class="column">
					<metric label="Revenue/Click" :value="metrics.rpc || 0" is-currency />
				</div>
			</row>
		</div>
		<gutter size="20px" />
		<row>
			<column>
				<div class="card padded chart-container">
					<p-chart ref="monetization_chart" :height="250" type="bar" :data="chartData" :options="chartOptions(true)" />
				</div>
			</column>
			<gutter size="20px" />
			<column>
				<div class="card padded chart-container">
					<p-chart
						ref="ad_pie_monetization_chart"
						:height="300"
						type="pie"
						:data="pieChartData"
						:options="pieChartOptions()"
					/>
				</div>
			</column>
			<gutter size="20px" />
			<column>
				<div class="card padded chart-container">
					<p-chart
						ref="expected_pie_monetization_chart"
						:height="300"
						type="doughnut"
						:data="expectedPieChartData"
						:options="pieChartOptions()"
					/>
				</div>
			</column>
		</row>
		<gutter size="20px" />
		<div class="card padded">
			<h2>Network Performance by Source</h2>
			<!-- Ad Network performance per source -->
			<p-data-table ref="ad-network-performance" :loading="loading" :value="ad_network_performance">
				<template #loading>
					<line-loader :show="loading" />
				</template>
				<template #empty> <div class="dim">No Data</div> </template>
				<p-column header="Source ID" sortable field="source_id">
					<template #body="row">
						{{ row.data.source_id }}
					</template>
				</p-column>
				<p-column header="Impressions" sortable field="impressions" class="align-center">
					<template #body="row">
						{{ row.data.impressions }}
					</template>
				</p-column>
				<p-column header="Clicks" sortable field="clicks" class="align-center">
					<template #body="row">
						{{ row.data.clicks }}
					</template>
				</p-column>
				<p-column header="Expected Monetization" sortable field="expected_revenue" class="align-center">
					<template #body="row">
						{{ currency(row.data.expected_revenue, 0) }}
					</template>
				</p-column>
				<p-column header="Ad Monetization" sortable field="ad_revenue" class="align-center">
					<template #body="row">
						{{ currency(row.data.ad_revenue, 0) }}
					</template>
				</p-column>
				<p-column header="Total Monetization" sortable field="total_revenue" class="align-center">
					<template #body="row">
						{{ currency(row.data.total_revenue, 0) }}
					</template>
				</p-column>
				<p-column header="Expected vs Total" sortable field="evt" class="align-center">
					<template #body="row">
						{{ currency(row.data.evt, 0) }}
						<div :class="['sub-value', trend(calculateDelta(row.data.expected_revenue, row.data.total_revenue))]">
							{{ percentage(calculateDelta(row.data.expected_revenue, row.data.total_revenue), 2) }}
						</div>
					</template>
				</p-column>
				<p-column sortable field="rpi" class="align-center">
					<template #header>
						<span v-tooltip.top="'Revenue/Impression'">RPI</span>
					</template>
					<template #body="row">
						{{ currency(row.data.rpi, 2) }}
					</template>
				</p-column>
				<p-column sortable field="rpc" class="align-center">
					<template #header>
						<span v-tooltip.top="'Revenue/Click'">RPC</span>
					</template>
					<template #body="row">
						{{ currency(row.data.rpc, 2) }}
					</template>
				</p-column>
			</p-data-table>
		</div>
	</div>
</template>

<script lang="ts">
import { currency, formatDate, title, percentage, displaySelected } from '@/lib/Filters';
import { get, merge } from 'lodash-es';
import STATES from '@/lib/Data/states.json';
import queryForm from '@/components/widgets/QueryForm.vue';
import tableTools from '@/components/widgets/TableTools.vue';
import dateRangePicker from '@/components/forms/DateRangePicker.vue';
import metric from '@/components/widgets/Metric.vue';
import dayjs from 'dayjs';
import pChart from 'primevue/chart';
import {
	getAdAuctionNetworksAsOptions,
	getAdAuctionPlacementsAsOptions,
	reportAdAuctionNetworkPerformance,
} from '@GQL';

export default {
	name: 'ad-network-performance',
	components: {
		dateRangePicker,
		metric,
		queryForm,
		tableTools,
		pChart,
	},
	inject: ['mq'],
	data() {
		return {
			loading: false,
			placement_options: [],
			ad_network_options: [],
			state_options: STATES,
			query: {
				date_range: [dayjs().subtract(1, 'week').toDate(), dayjs().endOf('day').toDate()],
				filters: {
					network_id: '',
					placement: [],
					vertical_id: [],
					state: [],
				},
			},
			total_rows: 0,
			ad_network_performance: [],
			metrics: {},
		};
	},
	methods: {
		get,
		currency,
		formatDate,
		title,
		percentage,
		displaySelected,
		async getData(refresh = false) {
			this.loading = true;
			try {
				const results = await reportAdAuctionNetworkPerformance(this.query, true);
				// Add Expected vs Total row
				this.ad_network_performance = results.map((row) => {
					return {
						...row,
						evt: row.expected_revenue - row.total_revenue,
						rpi: row.ad_revenue / row.impressions,
						rpc: row.ad_revenue / row.clicks,
					};
				});

				// Totals
				this.metrics = this.ad_network_performance.reduce(
					(acc, row) => {
						acc.impressions += parseInt(row.impressions);
						acc.clicks += parseInt(row.clicks);
						acc.expected_revenue += parseFloat(row.expected_revenue);
						acc.ad_revenue += parseFloat(row.ad_revenue);
						acc.total_revenue += parseFloat(row.total_revenue);
						return acc;
					},
					{
						impressions: 0,
						clicks: 0,
						expected_revenue: 0,
						ad_revenue: 0,
						total_revenue: 0,
					}
				);
				this.metrics.evt = this.metrics.expected_revenue - this.metrics.total_revenue;
				this.metrics.rpi = this.metrics.ad_revenue / this.metrics.impressions;
				this.metrics.rpc = this.metrics.ad_revenue / this.metrics.clicks;
			} finally {
				this.loading = false;
			}
		},
		calculateDelta(baseline, value) {
			return baseline / value - 1;
		},
		trend(value) {
			if (Math.abs(value) > 0.05) {
				if (value > 0) return 'trending-up';
				if (value < 0) return 'trending-down';
			}
			return '';
		},
		chartOptions(is_currency = false) {
			const options = {
				responsive: true,
				plugins: {
					legend: {
						display: false,
					},
				},
				x: {
					stacked: true,
				},
				y: {
					stacked: true,
				},
			};

			if (is_currency) {
				merge(options, {
					plugins: {
						tooltip: {
							callbacks: {
								label(context) {
									return `${context.dataset.label}: ${currency(context.dataset.data[context.dataIndex])}`;
								},
							},
						},
					},
					scales: {
						y: {
							ticks: {
								callback(value, index) {
									return currency(value);
								},
							},
						},
					},
				});
			}

			return options;
		},
		pieChartOptions() {
			return {
				responsive: true,
				plugins: {
					legend: {
						display: true,
					},
					tooltip: {
						callbacks: {
							label(context) {
								return `${context.label}: ${currency(context.raw)}`;
							},
						},
					},
				},
			};
		},
	},
	async mounted() {
		this.ad_network_options = await getAdAuctionNetworksAsOptions();
		this.placement_options = await getAdAuctionPlacementsAsOptions();
	},
	computed: {
		chartData() {
			const documentStyle = getComputedStyle(document.documentElement);

			return {
				labels: ['Ad', 'Expected', 'Total'],
				datasets: [
					{
						type: 'bar',
						label: 'Monetization',
						backgroundColor: [
							documentStyle.getPropertyValue('--green-600'),
							documentStyle.getPropertyValue('--orange-500'),
							documentStyle.getPropertyValue('--navy'),
						],
						opacity: 0.5,
						data: [
							get(this.metrics, 'ad_revenue', 0),
							get(this.metrics, 'expected_revenue', 0),
							get(this.metrics, 'total_revenue', 0),
						],
						options: {
							plugins: {
								legend: false,
							},
						},
					},
				],
			};
		},
		pieChartData() {
			const documentStyle = getComputedStyle(document.documentElement);
			return {
				labels: ['Ad', 'Total'],
				datasets: [
					{
						type: 'pie',
						label: 'Monetization',
						backgroundColor: [
							documentStyle.getPropertyValue('--green-600'),
							documentStyle.getPropertyValue('--green-200'),
						],
						data: [get(this.metrics, 'ad_revenue', 0), get(this.metrics, 'total_revenue', 0)],
					},
				],
			};
		},
		expectedPieChartData() {
			const documentStyle = getComputedStyle(document.documentElement);
			return {
				labels: ['Expected', 'Total'],
				datasets: [
					{
						type: 'doughnut',
						label: 'Monetization',
						backgroundColor: [documentStyle.getPropertyValue('--orange-500'), documentStyle.getPropertyValue('--navy')],
						data: [get(this.metrics, 'expected_revenue', 0), get(this.metrics, 'total_revenue', 0)],
					},
				],
			};
		},
	},
};
</script>

<style lang="less" scoped>
@import (reference) '@/styles/responsive';
.chart-container {
	display: flex;
	justify-content: center;
	align-items: center;
	max-width: 100%;
	height: 100%;
}
</style>
